'use client';

import { useEffect } from 'react';
import { FACEBOOK_APP_ID, FACEBOOK_API_VERSION } from '@/config';

declare global {
  interface Window {
    fbAsyncInit?: () => void;
    FB?: {
      init: (params: {
        appId: string;
        cookie: boolean;
        xfbml: boolean;
        version: string;
      }) => void;
      AppEvents?: {
        logPageView: () => void;
      };
      getLoginStatus?: (callback: (response: any) => void) => void;
      login?: (callback: (response: any) => void, params?: any) => void;
      api?: (path: string, callback: (response: any) => void) => void;
    };
  }
}

export function FacebookSDK() {
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    // Store the original fbAsyncInit if it exists
    const originalFbAsyncInit = window.fbAsyncInit;

    window.fbAsyncInit = function() {
      try {
        // Call the original fbAsyncInit if it exists
        if (originalFbAsyncInit) {
          originalFbAsyncInit();
        }

        // Initialize the Facebook SDK
        if (window.FB) {
          window.FB.init({
            appId: FACEBOOK_APP_ID || '',
            cookie: true,
            xfbml: true,
            version: FACEBOOK_API_VERSION || 'v17.0'
          });

          // Log page view if AppEvents is available
          if (window.FB.AppEvents) {
            window.FB.AppEvents.logPageView();
          }
        }
      } catch (error) {
        // Silently fail if FB SDK initialization fails
        console.warn('Facebook SDK initialization failed:', error);
      }
    };

    // Load the Facebook SDK script if not already loaded
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';

      // Add error handler for script loading failures
      script.onerror = () => {
        console.warn('Failed to load Facebook SDK script');
      };

      const firstScript = document.getElementsByTagName('script')[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      }
    }
  }, []);

  return null; // This component doesn't render anything
}
